import dayjs from 'dayjs';
import { toEnumFromString, fromEnumAsString } from '../../serialization/enum-utils.js';
import { MapUtils } from '../../utility/map-utils.js';
import { TimeZone } from '../../time/timezone.js';
import { VersionRange } from '../core/version-range.js';
import { SerializationContext, DataModelBuilder, SerializedData } from '../dto/dto.js';
import { SyncEvent, SyncEventSource } from '../sync-event.js';

export class BusinessDeletedEvent extends SyncEvent {
  static readonly TYPE_ID: string = 'f4b36290fcbe401aa2711cc471016827';
  static readonly SCHEMA_VERSION: number = 2;

  constructor(
    public readonly uniqueId: string,
    public readonly creatorId: string | null,
    public readonly deviceId: string | null,
    public readonly creationDate: dayjs.Dayjs,
    public readonly source: SyncEventSource,
    public readonly businessId: string,
  ) {
    super();
  }

  get $typeId(): string {
    return BusinessDeletedEvent.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.between(1, BusinessDeletedEvent.SCHEMA_VERSION);
  }

  static fromMap(context: SerializationContext, map: SerializedData): BusinessDeletedEvent {
    return new BusinessDeletedEvent(
        MapUtils.get<string>(map, 'uniqueId'),
        MapUtils.getOrNull<string>(map, 'creatorId'),
        MapUtils.getOrNull<string>(map, 'deviceId'),
        context.decodeDate(MapUtils.get<number>(map, 'creationDate')),
        toEnumFromString(MapUtils.get<string>(map, 'source'), SyncEventSource),
        MapUtils.get<string>(map, 'businessId'),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'uniqueId': this.uniqueId,
      'creatorId': this.creatorId,
      'deviceId': this.deviceId,
      'creationDate': context.encodeDate(this.creationDate),
      'source': fromEnumAsString(this.source),
      'businessId': this.businessId,
    };
  }

  clone(): this {
    return new BusinessDeletedEvent(
        this.uniqueId,
        this.creatorId,
        this.deviceId,
        TimeZone.cloneDate(this.creationDate),
        this.source,
        this.businessId,
    ) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return other.uniqueId === this.uniqueId &&
        other.creatorId === this.creatorId &&
        other.deviceId === this.deviceId &&
        other.creationDate === this.creationDate &&
        other.source === this.source &&
        other.businessId === this.businessId;
  }
}

export class BusinessDeletedEventBuilder extends DataModelBuilder<BusinessDeletedEvent> {
  build(context: SerializationContext, map: SerializedData): BusinessDeletedEvent {
    return BusinessDeletedEvent.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);
    if(versionRange.contains(BusinessDeletedEvent.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(BusinessDeletedEvent.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    /**
     * Version 2:
     *  - add `deviceId` of type: string | null
     */
    if(version < 2) {
      version = 2;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
